/** The install prompt's timing: once, on the third session, and never again.
 *
 *  "Shown once, on the third session, never over the capture bar" — the design's
 *  own words. Two sessions of grace before asking anything, and a refusal is
 *  final: `NOT NOW` is a decision, not a snooze, because a prompt that returns
 *  is the pattern this product exists in opposition to.
 *
 *  Session counting, not visit counting: a reload inside one sitting is the same
 *  session, so the count advances once per app start.
 */

const KEY = 'bujo.install.v1';

interface InstallState {
  sessions: number;
  /** Set when it has been shown, whichever way the answer went. */
  settled: boolean;
}

const PROMPT_ON_SESSION = 3;

function read(): InstallState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { sessions: 0, settled: false };
    const parsed = JSON.parse(raw) as Partial<InstallState>;
    return {
      sessions: typeof parsed.sessions === 'number' ? parsed.sessions : 0,
      settled: parsed.settled === true,
    };
  } catch {
    return { sessions: 0, settled: false };
  }
}

function write(state: InstallState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private browsing refuses storage. Never prompting is the safe failure.
  }
}

/** Call once per app start. Returns whether the prompt is due now. */
export function startSession(): boolean {
  const state = read();
  const sessions = state.sessions + 1;
  write({ ...state, sessions });
  if (state.settled) return false;
  // Already installed: a standalone window has nothing to ask for.
  if (matchMedia('(display-mode: standalone)').matches) return false;
  return sessions === PROMPT_ON_SESSION;
}

/** Shown and answered — either way, it does not come back. */
export function settle(): void {
  write({ ...read(), settled: true });
}
